/**
 * Client Retention review tabs (WS3).
 *
 * Four tabs mapped to the (status, relationship) filter tuple that the
 * getRefinanceMatches callable already supports (MatchFilters.status +
 * MatchFilters.relationship). Mounted additively in BOTH RefinanceIntelligence
 * page variants; `reviewTabToFilters` lives here so the two near-duplicate
 * pages derive identical filters and cannot drift apart.
 *
 * The Needs Review (pending) tab additionally carries a relationship sub-filter
 * (All / Refinances / Returning Clients) so reviewers can triage pending
 * refinances separately from pending returning-clients — the wider detection
 * window (-120..+400d) surfaces both into one pending queue. The sub-filter is
 * pending-only; the confirmed Refinances / Returning Clients tabs are already
 * split by relationship on their own.
 */
import React from 'react';
import { AlertCircle, Repeat, Users, XCircle } from 'lucide-react';
import type { MatchStatus, RelationshipType } from '../../hooks/useRefinanceData';

export type ReviewTab = 'needs_review' | 'refinances' | 'returning' | 'rejected';

/** Needs-Review pending sub-filter: 'all' plus the two relationship values. */
export type PendingRelationship = 'all' | RelationshipType;

export interface ReviewTabCounts {
  needsReview: number;
  refinances: number;
  returning: number;
  rejected: number;
}

/**
 * Translate a review tab into the filter tuple the backend understands.
 * Legacy confirmed rows carry NULL relationship_type which getRefinanceMatches
 * treats as 'refinance' (COALESCE), so the Refinances tab includes them.
 *
 * `pendingRelationship` narrows ONLY the Needs Review (pending) tab; 'all'
 * leaves relationship undefined (the prior behaviour — every pending match).
 */
export function reviewTabToFilters(
  tab: ReviewTab,
  pendingRelationship: PendingRelationship = 'all'
): { status: MatchStatus | 'all'; relationship?: RelationshipType } {
  switch (tab) {
    case 'needs_review':
      return {
        status: 'pending',
        relationship: pendingRelationship === 'all' ? undefined : pendingRelationship,
      };
    case 'refinances':
      return { status: 'confirmed', relationship: 'refinance' };
    case 'returning':
      return { status: 'confirmed', relationship: 'returning_client' };
    case 'rejected':
      return { status: 'rejected' };
  }
}

interface TabDef {
  id: ReviewTab;
  label: string;
  icon: React.FC<{ className?: string }>;
  activeClass: string;
  countKey: keyof ReviewTabCounts;
}

const TABS: TabDef[] = [
  { id: 'needs_review', label: 'Needs Review', icon: AlertCircle, activeClass: 'text-yellow-400 border-yellow-400', countKey: 'needsReview' },
  { id: 'refinances', label: 'Refinances', icon: Repeat, activeClass: 'text-cyan-400 border-cyan-400', countKey: 'refinances' },
  { id: 'returning', label: 'Returning Clients', icon: Users, activeClass: 'text-purple-400 border-purple-400', countKey: 'returning' },
  { id: 'rejected', label: 'Rejected', icon: XCircle, activeClass: 'text-red-400 border-red-400', countKey: 'rejected' },
];

const PENDING_SUBFILTERS: { id: PendingRelationship; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'refinance', label: 'Refinances' },
  { id: 'returning_client', label: 'Returning Clients' },
];

interface ReviewTabsProps {
  activeTab: ReviewTab;
  onTabChange: (tab: ReviewTab) => void;
  counts: ReviewTabCounts;
  /** Needs-Review pending sub-filter value + setter (pending-only). */
  pendingRelationship: PendingRelationship;
  onPendingRelationshipChange: (value: PendingRelationship) => void;
}

const ReviewTabs: React.FC<ReviewTabsProps> = ({
  activeTab,
  onTabChange,
  counts,
  pendingRelationship,
  onPendingRelationshipChange,
}) => {
  return (
    <div>
      <div className="border-b border-gray-700">
        <div className="flex flex-wrap gap-1" role="tablist" aria-label="Client retention review">
          {TABS.map(({ id, label, icon: Icon, activeClass, countKey }) => {
            const isActive = activeTab === id;
            return (
              <button
                key={id}
                role="tab"
                aria-selected={isActive}
                onClick={() => onTabChange(id)}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
                  isActive ? activeClass : 'text-gray-400 border-transparent hover:text-gray-200 hover:border-gray-600'
                }`}
              >
                <Icon className="w-4 h-4" />
                {label}
                <span
                  className={`text-xs px-1.5 py-0.5 rounded-full ${
                    isActive ? 'bg-gray-700 text-gray-100' : 'bg-gray-800 text-gray-400'
                  }`}
                >
                  {counts[countKey]}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {activeTab === 'needs_review' && (
        <div
          className="flex flex-wrap items-center gap-2 pt-3 text-xs"
          role="group"
          aria-label="Filter pending review by relationship"
        >
          <span className="text-gray-400">Show:</span>
          {PENDING_SUBFILTERS.map(({ id, label }) => {
            const active = pendingRelationship === id;
            return (
              <button
                key={id}
                type="button"
                aria-pressed={active}
                onClick={() => onPendingRelationshipChange(id)}
                className={`px-2.5 py-1 rounded-full border transition-colors ${
                  active
                    ? 'bg-cyan-500/10 text-cyan-300 border-cyan-500/40'
                    : 'text-gray-400 border-gray-700 hover:text-gray-200 hover:border-gray-600'
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ReviewTabs;
